// src/context/AccountTypeContext.tsx - Fixed to work with Router
import React, {createContext, useContext, useState, ReactNode, useMemo, useCallback, useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import type {AccountType} from '@/types';
import {getAccountTypeLabels} from '@/utils/accountTypeLabels';

interface AccountTypeContextValue {
    accountType: AccountType;
    setAccountType: (type: AccountType) => void;
    labels: ReturnType<typeof getAccountTypeLabels>;
}

const AccountTypeContext = createContext<AccountTypeContextValue | undefined>(undefined);

// Base provider that doesn't use useLocation
export const AccountTypeProvider: React.FC<{ children: ReactNode }> = ({children}) => {
    const [accountType, setAccountType] = useState<AccountType>(() => {
        try {
            return (localStorage.getItem('accountType') as AccountType) || 'academic';
        } catch {
            return 'academic';
        }
    });

    const labels = useMemo(() => getAccountTypeLabels(accountType), [accountType]);

    const handleSetAccountType = useCallback((type: AccountType) => {
        setAccountType(type);
        try {
            localStorage.setItem('accountType', type);
        } catch (error) {
            console.warn('Could not save account type to localStorage:', error);
        }
    }, []);

    const value = useMemo(() => ({
        accountType,
        setAccountType: handleSetAccountType,
        labels
    }), [accountType, handleSetAccountType, labels]);

    return (
        <AccountTypeContext.Provider value={value}>
            {children}
        </AccountTypeContext.Provider>
    );
};

// URL-aware component that must be inside Router
export const AccountTypeUrlSync: React.FC = () => {
    const location = useLocation();
    const {setAccountType, accountType} = useAccountType();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlType = params.get('type') as AccountType;

        // If URL has a valid type parameter, update the context
        if (urlType === 'academic' || urlType === 'business') {
            if (urlType !== accountType) {
                setAccountType(urlType);
            }
        }
        // If no type parameter and we're on a page that should have one, default to academic
        else if (location.pathname === '/organizations' ||
            location.pathname === '/accounts' ||
            location.pathname === '/game-hosts') {
            if (accountType !== 'academic') {
                setAccountType('academic');
            }
        }
    }, [location.search, location.pathname, setAccountType, accountType]);

    return null; // This component doesn't render anything
};

export const useAccountType = () => {
    const context = useContext(AccountTypeContext);
    if (!context) {
        throw new Error('useAccountType must be used within AccountTypeProvider');
    }
    return context;
};
